import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { io } from "socket.io-client";
import { apiFetch, langFromPath } from "../api";
import { Navbar, Btn, StatusDot, C } from "../components/UI";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";

export default function ProjectIDE() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [openFile, setOpenFile] = useState(null);
  const [code, setCode] = useState("");
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [starting, setStarting] = useState(false);
  const [tab, setTab] = useState("editor"); // editor | terminal | preview | agent
  const [agentMessages, setAgentMessages] = useState([]);
  const [agentInput, setAgentInput] = useState("");
  const [agentLoading, setAgentLoading] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [showNewFile, setShowNewFile] = useState(false);

  const socketRef = useRef(null);
  const termRef = useRef(null);
  const termDivRef = useRef(null);
  const fitRef = useRef(null);
  const agentEndRef = useRef(null);
  const saveTimer = useRef(null);

  // Load project + files
  useEffect(() => {
    apiFetch(`/projects/${id}`).then(p => {
      setProject(p);
      if (p.container_status === "running") {
        const host = window.location.hostname;
        setPreviewUrl(`https://${p.subdomain}.${host}`);
      }
    });
    apiFetch(`/projects/${id}/files`).then(f => {
      setFiles(f);
      if (f.length > 0 && !openFile) openFileHandler(f[0]);
    });
    apiFetch(`/projects/${id}/agent-history`).then(h =>
      setAgentMessages(h.map(m => ({ role:m.role, content:m.content })))
    );
  }, [id]);

  // Socket setup
  useEffect(() => {
    const token = localStorage.getItem("token");
    const socket = io("/", { auth:{ token }, path:"/socket.io" });
    socketRef.current = socket;

    // Agent events
    socket.on("agent:event", (event) => {
      if (event.type === "text") {
        setAgentMessages(prev => {
          const last = prev[prev.length-1];
          if (last?.role === "assistant" && last.streaming) {
            return [...prev.slice(0,-1), { role:"assistant", content: last.content + event.text, streaming:true }];
          }
          return [...prev, { role:"assistant", content:event.text, streaming:true }];
        });
      } else if (event.type === "tool_start") {
        setAgentMessages(prev => [...prev, { role:"tool", tool:event.tool, input:event.input }]);
      } else if (event.type === "done") {
        setAgentMessages(prev => prev.map(m => ({ ...m, streaming:false })));
        setAgentLoading(false);
        // Reload files
        apiFetch(`/projects/${id}/files`).then(setFiles);
      } else if (event.type === "tool_result" && event.tool === "write_file") {
        apiFetch(`/projects/${id}/files`).then(setFiles);
      }
    });

    socket.on("agent:error", (err) => {
      setAgentMessages(prev => [...prev, { role:"error", content:err }]);
      setAgentLoading(false);
    });

    return () => socket.disconnect();
  }, [id]);

  // Terminal setup
  useEffect(() => {
    if (tab !== "terminal" || !termDivRef.current || termRef.current) return;
    const term = new Terminal({
      theme: { background:"#0d0d0f", foreground:"#e8e6e1", cursor:"#f0ec57" },
      fontFamily:"'JetBrains Mono', monospace", fontSize:13, cursorBlink:true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(termDivRef.current);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;

    const socket = socketRef.current;
    socket.emit("terminal:start", id);
    socket.on("terminal:ready", () => term.write("Connected to container\r\n$ "));
    socket.on("terminal:data", (data) => term.write(data));
    socket.on("terminal:exit", () => term.write("\r\n[Session ended]"));
    socket.on("terminal:error", (e) => term.write(`\r\nError: ${e}\r\n`));

    term.onData(data => socket.emit("terminal:input", data));

    const ro = new ResizeObserver(() => { try { fit.fit(); socket.emit("terminal:resize", { cols:term.cols, rows:term.rows }); } catch {} });
    ro.observe(termDivRef.current);

    return () => { ro.disconnect(); term.dispose(); termRef.current = null; socket.off("terminal:ready"); socket.off("terminal:data"); socket.off("terminal:exit"); };
  }, [tab, id]);

  const openFileHandler = (f) => {
    if (dirty && openFile) saveFile(openFile, code);
    setOpenFile(f);
    setCode(f.content);
    setDirty(false);
  };

  const saveFile = useCallback(async (file, content) => {
    if (!file) return;
    const path = file.path;
    await apiFetch(`/projects/${id}/files/${path}`, { method:"PUT", body:JSON.stringify({ content }) });
    setFiles(prev => prev.map(f => f.path===path ? {...f, content} : f));
    setDirty(false);
  }, [id]);

  const handleCodeChange = (value) => {
    setCode(value || "");
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveFile(openFile, value || ""), 1500);
  };

  const startProject = async () => {
    setStarting(true);
    try {
      const r = await apiFetch(`/projects/${id}/start`, { method:"POST" });
      setPreviewUrl(r.previewUrl);
      setProject(p => ({...p, container_status:"running"}));
      setTab("preview");
    } catch(e) { alert(e.message); }
    setStarting(false);
  };

  const stopProject = async () => {
    await apiFetch(`/projects/${id}/stop`, { method:"POST" });
    setProject(p => ({...p, container_status:"stopped"}));
    setPreviewUrl(null);
  };

  const createFile = async (e) => {
    e.preventDefault();
    if (!newFileName.trim()) return;
    const path = newFileName.trim();
    await apiFetch(`/projects/${id}/files/${path}`, { method:"PUT", body:JSON.stringify({ content:"" }) });
    const newF = { path, content:"" };
    setFiles(prev => [...prev, newF]);
    openFileHandler(newF);
    setNewFileName(""); setShowNewFile(false);
  };

  const sendAgentMessage = async (e) => {
    e.preventDefault();
    if (!agentInput.trim() || agentLoading) return;
    const msg = agentInput.trim();
    setAgentInput("");
    setAgentMessages(prev => [...prev, { role:"user", content:msg }]);
    setAgentLoading(true);
    setTimeout(() => agentEndRef.current?.scrollIntoView({ behavior:"smooth" }), 50);

    if (project?.container_status !== "running") {
      setAgentMessages(prev => [...prev, { role:"error", content:"Start the project first before using the agent." }]);
      setAgentLoading(false);
      return;
    }
    socketRef.current?.emit("agent:message", { projectId:id, message:msg });
  };

  // ── Layout ─────────────────────────────────────────────────────────────────
  const tabs = ["editor","terminal","preview","agent"];

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", background:C.bg }}>
      <Navbar />

      {/* Top bar */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", padding:"0 1rem", height:"44px",
        background:C.surface, borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
        <StatusDot status={project?.container_status || "stopped"} />
        <span style={{ fontWeight:600, fontSize:"0.88rem" }}>{project?.name || "…"}</span>
        <span style={{ fontSize:"0.72rem", color:C.muted, background:"#1a1a22",
          border:`1px solid ${C.border}`, borderRadius:"4px", padding:"1px 7px" }}>
          {project?.template}
        </span>

        <div style={{ flex:1 }} />

        {previewUrl && (
          <a href={previewUrl} target="_blank" rel="noreferrer"
            style={{ fontSize:"0.75rem", color:C.muted, textDecoration:"none", display:"flex", alignItems:"center", gap:"4px" }}>
            🌐 {previewUrl}
          </a>
        )}

        {project?.container_status !== "running" ? (
          <Btn onClick={startProject} loading={starting} small>▶ Run Project</Btn>
        ) : (
          <Btn onClick={stopProject} variant="danger" small>■ Stop</Btn>
        )}
      </div>

      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
        {/* File tree */}
        <div style={{ width:"200px", flexShrink:0, borderRight:`1px solid ${C.border}`,
          background:C.surface, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          <div style={{ padding:"8px 10px", display:"flex", alignItems:"center", justifyContent:"space-between",
            borderBottom:`1px solid ${C.border}` }}>
            <span style={{ fontSize:"0.68rem", textTransform:"uppercase", letterSpacing:"0.1em", color:C.muted }}>Files</span>
            <button onClick={() => setShowNewFile(v=>!v)}
              style={{ background:"none", border:"none", color:C.muted, fontSize:"1.1rem", lineHeight:1, cursor:"pointer" }}>+</button>
          </div>
          {showNewFile && (
            <form onSubmit={createFile} style={{ padding:"6px 8px", borderBottom:`1px solid ${C.border}` }}>
              <input autoFocus value={newFileName} onChange={e=>setNewFileName(e.target.value)}
                placeholder="filename.py" onKeyDown={e=>e.key==="Escape"&&setShowNewFile(false)}
                style={{ width:"100%", background:"#1a1a22", border:`1px solid ${C.accent}`, color:C.text,
                  borderRadius:"4px", padding:"4px 7px", fontSize:"0.78rem" }} />
            </form>
          )}
          <div style={{ flex:1, overflowY:"auto", padding:"4px 0" }}>
            {files.map(f => (
              <div key={f.path} onClick={() => { openFileHandler(f); setTab("editor"); }}
                style={{ padding:"5px 12px", cursor:"pointer", fontSize:"0.78rem",
                  fontFamily:"'JetBrains Mono',monospace",
                  background: openFile?.path===f.path ? "#1e1e30" : "transparent",
                  color: openFile?.path===f.path ? C.text : C.muted,
                  borderLeft: openFile?.path===f.path ? `2px solid ${C.accent}` : "2px solid transparent",
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {f.path}
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {/* Tabs */}
          <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, background:C.surface, flexShrink:0 }}>
            {tabs.map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{ background:"none", border:"none", color:tab===t?C.accent:C.muted,
                  fontFamily:"'Syne',sans-serif", fontWeight:tab===t?600:400, fontSize:"0.8rem",
                  padding:"9px 18px", borderBottom:tab===t?`2px solid ${C.accent}`:"2px solid transparent",
                  marginBottom:"-1px", textTransform:"capitalize", cursor:"pointer" }}>
                {t === "agent" ? "🤖 Agent" : t === "terminal" ? "⌨️ Terminal" : t === "preview" ? "🌐 Preview" : "📝 Editor"}
              </button>
            ))}
            {dirty && <span style={{ alignSelf:"center", marginLeft:"8px", fontSize:"0.72rem", color:C.muted }}>● unsaved</span>}
            {dirty && openFile && (
              <Btn small variant="ghost" style={{ alignSelf:"center", marginLeft:"auto", marginRight:"10px" }}
                onClick={() => saveFile(openFile, code)}>Save</Btn>
            )}
          </div>

          {/* Editor */}
          <div style={{ flex:1, display:tab==="editor"?"flex":"none", overflow:"hidden" }}>
            {openFile ? (
              <Editor height="100%" language={langFromPath(openFile.path)} value={code}
                onChange={handleCodeChange} theme="vs-dark"
                options={{ fontSize:13, fontFamily:"'JetBrains Mono',monospace", minimap:{enabled:false},
                  scrollBeyondLastLine:false, padding:{top:12} }} />
            ) : (
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:C.muted, fontSize:"0.88rem" }}>
                Select a file to edit
              </div>
            )}
          </div>

          {/* Terminal */}
          <div style={{ flex:1, display:tab==="terminal"?"flex":"none", overflow:"hidden", padding:"8px" }}>
            <div ref={termDivRef} style={{ flex:1, borderRadius:"8px", overflow:"hidden" }} />
          </div>

          {/* Preview */}
          <div style={{ flex:1, display:tab==="preview"?"flex":"none", flexDirection:"column", overflow:"hidden" }}>
            {previewUrl ? (
              <iframe src={previewUrl} style={{ flex:1, border:"none", background:"#fff" }} title="preview" />
            ) : (
              <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"1rem", color:C.muted }}>
                <div style={{ fontSize:"2rem" }}>🚀</div>
                <div style={{ fontSize:"0.88rem" }}>Click "Run Project" to start your app and see the preview</div>
                <Btn onClick={startProject} loading={starting}>▶ Run Project</Btn>
              </div>
            )}
          </div>

          {/* Agent */}
          <div style={{ flex:1, display:tab==="agent"?"flex":"none", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ flex:1, overflowY:"auto", padding:"1rem", display:"flex", flexDirection:"column", gap:"10px" }}>
              {agentMessages.length === 0 && (
                <div style={{ color:C.muted, fontSize:"0.85rem", textAlign:"center", paddingTop:"2rem" }}>
                  <div style={{ fontSize:"2rem", marginBottom:"0.5rem" }}>🤖</div>
                  <div style={{ fontWeight:600, marginBottom:"4px" }}>Claude Agent</div>
                  <div>Describe what you want to build or change.<br />The agent will read your files, write code, and run commands.</div>
                  <div style={{ marginTop:"1rem", fontSize:"0.78rem", color:"#444" }}>
                    {project?.container_status !== "running" && "⚠️ Start the project first for the agent to work."}
                  </div>
                </div>
              )}
              {agentMessages.map((m, i) => (
                m.role === "tool" ? (
                  <div key={i} style={{ background:"#0a0a18", border:`1px solid ${C.border}`, borderRadius:"7px",
                    padding:"7px 12px", fontSize:"0.75rem", fontFamily:"'JetBrains Mono',monospace", color:"#888" }}>
                    🔧 {m.tool}({m.input?.path || m.input?.command || ""})
                  </div>
                ) : m.role === "error" ? (
                  <div key={i} style={{ background:"#2a1010", border:`1px solid ${C.error}`, borderRadius:"8px",
                    padding:"10px 14px", fontSize:"0.83rem", color:C.error }}>⚠️ {m.content}</div>
                ) : (
                  <div key={i} style={{
                    alignSelf: m.role==="user" ? "flex-end" : "flex-start",
                    background: m.role==="user" ? "#1e1c00" : C.surface,
                    border: `1px solid ${m.role==="user" ? C.accent+"33" : C.border}`,
                    borderRadius:"10px", padding:"10px 14px", maxWidth:"85%",
                    fontSize:"0.84rem", lineHeight:1.7, whiteSpace:"pre-wrap",
                    color: m.role==="user" ? C.accent : C.text,
                  }}>
                    {m.content}
                    {m.streaming && <span style={{ opacity:0.5 }}>▋</span>}
                  </div>
                )
              ))}
              <div ref={agentEndRef} />
            </div>
            <form onSubmit={sendAgentMessage}
              style={{ padding:"10px", borderTop:`1px solid ${C.border}`, display:"flex", gap:"8px", background:C.surface }}>
              <input value={agentInput} onChange={e=>setAgentInput(e.target.value)}
                placeholder={agentLoading ? "Agent is working…" : "Ask Claude to build something…"}
                disabled={agentLoading}
                style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
                  borderRadius:"8px", padding:"10px 14px", fontFamily:"'Syne',sans-serif", fontSize:"0.85rem" }} />
              <Btn type="submit" loading={agentLoading}>Send</Btn>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
